// =============================================================================
// AuditTimeline — the "Geschiedenis" block shared by the booking and voucher
// detail pages. One card, newest first, a rail with a dot per event, the
// actor as a truncating pill (long e-mails used to overflow into the action
// column), a Dutch label per action and a readable detail line instead of
// raw JSON.
// =============================================================================
import type { ReactNode } from 'react';
import { formatVenueDateTime } from '../lib/time';

export type AuditActorType =
  | 'customer'
  | 'operator'
  | 'system'
  | 'stripe'
  | 'sweeper'
  | 'voucher-confirm';

export interface AuditTimelineItem {
  id: string;
  created_at: string;
  actor_type: AuditActorType | string;
  actor_label: string | null;
  action: string;
  // Pre-rendered detail line. When omitted, `value` is formatted generically.
  detail?: ReactNode;
  value?: unknown;
  // Derived from state tables rather than a real audit_log row.
  derived?: boolean;
}

// Human labels for the actions the booking + voucher flows produce. Unknown
// actions fall back to the raw name with underscores as spaces.
const ACTION_LABELS: Record<string, string> = {
  // bookings
  walkin_created:                'Walk-in aangemaakt',
  slot_rescheduled:              'Verzet',
  admin_cancelled:               'Geannuleerd',
  admin_edited_slots:            'Slots bewerkt',
  admin_slots_edited:            'Slots bewerkt',
  admin_reduced_party:           'Aantal personen verlaagd',
  admin_marked_paid:             'Gemarkeerd als betaald',
  admin_refunded:                'Terugbetaald',
  admin_resent_email:            'Bevestiging opnieuw gemaild',
  admin_sent_payment_link:       'Betaallink verstuurd',
  admin_refund_voucher:          'Tegoedbon uitgegeven',
  checkin_rescanned:             'Opnieuw gescand',
  payment_succeeded_unconfirmed: 'Betaling ontvangen, niet bevestigd',
  balance_paid_online:           'Restbedrag online betaald',
  balance_paid_pos:              'Restbedrag aan de balie betaald',
  lifecycle_changed:             'Status gewijzigd',
  payment_changed:               'Betaalstatus gewijzigd',
  slot_lifecycle_changed:        'Slotstatus gewijzigd',
  departure_play_language_set:   'Speeltaal ingesteld',
  pi_hold_swept:                 'Verlopen reservering opgeruimd',
  pi_hold_deferred:              'Opruimen uitgesteld (betaling loopt)',
  queue_enqueued:                'In de wachtrij gezet',
  queue_reordered:               'Wachtrij herschikt',
  queue_parked:                  'Geparkeerd',
  queue_unparked:                'Uit parkeerstand',
  queue_requeued:                'Opnieuw in de wachtrij',
  queue_undo:                    'Wachtrij-actie ongedaan gemaakt',
  // vouchers
  voucher_purchase_started:      'Aankoop gestart',
  voucher_purchase_paid:         'Betaald · code aangemaakt',
  voucher_payment_failed:        'Betaling mislukt',
  voucher_purchase_refunded:     'Terugbetaald · code gedeactiveerd',
  voucher_email_sent:            'Cadeaubon gemaild',
  admin_resent_voucher_email:    'Cadeaubon opnieuw gemaild',
  discount_code_redeemed:        'Ingewisseld',
  discount_code_created:         'Code aangemaakt',
  discount_code_updated:         'Code bewerkt',
  discount_code_marked_used:     'Handmatig als gebruikt gemarkeerd',
  discount_code_use_unmarked:    'Handmatige markering ongedaan gemaakt',
  discount_code_deactivated:     'Gedeactiveerd',
  // physical gift cards
  gift_card_batch_created:       'Drukbatch aangemaakt',
  gift_card_batch_exported:      'Codes gedownload voor de drukker',
  gift_card_load_started:        'Opwaarderen gestart',
  gift_card_activated:           'Geactiveerd',
  gift_card_loaded:              'Opgewaardeerd',
  gift_card_load_failed:         'Betaling mislukt',
  gift_card_load_canceled:       'Opwaarderen afgebroken',
  gift_card_load_refunded:       'Opwaardering terugbetaald',
  gift_card_blocked:             'Geblokkeerd',
  gift_card_unblocked:           'Gedeblokkeerd',
  gift_card_adjusted:            'Saldo gecorrigeerd',
  gift_card_redeemed:            'Besteed',
  gift_card_redeemed_pos:        'Besteed aan de balie',
  gift_card_removed_from_booking: 'Van boeking gehaald',
  gift_card_refund_credit:       'Teruggezet op de kaart',
  gift_card_settle_failed:       'Afboeken mislukt',
};

export function auditActionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action.replace(/_/g, ' ');
}

const ACTOR_LABELS: Record<string, string> = {
  customer:          'klant',
  operator:          'medewerker',
  system:            'systeem',
  stripe:            'Stripe',
  sweeper:           'opruimer',
  'voucher-confirm': 'voucher',
};

// Generic formatter for from_value / to_value payloads: a flat object becomes
// "key: value · key: value"; anything else is compact JSON.
export function formatAuditValue(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v !== 'object' || Array.isArray(v)) return JSON.stringify(v);
  const parts = Object.entries(v as Record<string, unknown>)
    .filter(([, val]) => val != null && val !== '')
    .map(([k, val]) => {
      const shown =
        typeof val === 'object' ? JSON.stringify(val) : String(val);
      return `${k.replace(/_/g, ' ')}: ${shown}`;
    });
  return parts.length > 0 ? parts.join(' · ') : null;
}

export function AuditTimeline({
  items,
  emptyText = 'Geen geschiedenis.',
}: {
  items: AuditTimelineItem[];
  emptyText?: string;
}) {
  if (items.length === 0) {
    return <div className="audit-timeline audit-timeline-empty muted">{emptyText}</div>;
  }
  return (
    <ol className="audit-timeline">
      {items.map((it) => {
        const detail = it.detail !== undefined ? it.detail : formatAuditValue(it.value);
        const actorType = it.actor_type;
        const actor = it.actor_label ?? ACTOR_LABELS[actorType] ?? actorType;
        return (
          <li key={it.id} className={`audit-item audit-item-${actorType}`}>
            <span className="audit-rail" aria-hidden="true"><span className="audit-dot" /></span>
            <div className="audit-body">
              <div className="audit-head">
                <span className="audit-action" title={it.action}>
                  {auditActionLabel(it.action)}
                </span>
                <span className={`audit-actor audit-actor-${actorType}`} title={actor}>
                  {actor}
                </span>
                {it.derived ? (
                  <span
                    className="audit-derived"
                    title="Afgeleid uit de aankoop- / inwisseltabellen, geen audit-regel"
                  >
                    afgeleid
                  </span>
                ) : null}
              </div>
              {detail ? <div className="audit-detail">{detail}</div> : null}
            </div>
            <time className="audit-time" dateTime={it.created_at}>
              {formatVenueDateTime(it.created_at)}
            </time>
          </li>
        );
      })}
    </ol>
  );
}
